<script>
import { OrdersMixin } from "@/views/orders/orders.mixin";
import OmniDialog from "@/components/OmniDialog.vue";
import ReorderTripStop from "@/views/trips/edit/blocks/ReorderTripStop.vue";
import { useOrder } from "@/composables/index.ts";
import dayjs from "dayjs";

const { orderColorList } = useOrder();

export default {
  name: "ReorderOrderStops",
  components: { ReorderTripStop, OmniDialog },
  mixins: [OrdersMixin],
  props: {
    trip: {
      type: Object,
      required: true,
    },
    tripManager: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Boolean,
      required: true,
    },
    newOrder: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "reorder"],
  data() {
    return {
      orderColors: {},
      tripStops: [],
      tripStopsKey: 0,
    };
  },
  computed: {
    model: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    orders() {
      return this.trip.orders;
    },
    routes() {
      return this.trip.routes;
    },
    routeStops() {
      return this.routes.flatMap((route) => route.routeStops);
    },
    orderStops() {
      return this.orders.flatMap((order) => order.orderStops);
    },
    ordersById() {
      return new Map(this.orders.map((order) => [order.id, order]));
    },
    orderFreights() {
      return this.orders.flatMap((order) => order.freights);
    },
    travelOrders() {
      return this.routes.flatMap((route) => route.travelOrders);
    },
    hasReload() {
      return this.tripStops.some((tripStop) => tripStop.isReload);
    },
    hasInvalidDateSequence() {
      return this.tripStops.some((stop, index, arr) => {
        if (index === 0) {
          return false;
        }

        const prev = dayjs(arr[index - 1].timeTo);
        const current = dayjs(stop.timeTo);

        return current.isBefore(prev);
      });
    },
    isPrimaryButtonDisabled() {
      return this.hasInvalidDateSequence;
    },
  },
  created() {
    this.createOrderColors();
    this.createTripStops();
  },
  methods: {
    createTripStops() {
      const tripStopsByOrderStopId = new Map(this.trip.tripStops.map((ts) => [ts.orderStopId, ts]));
      let maxExistingPosition = Math.max(...this.trip.tripStops.map((tripStop) => tripStop.position));

      const normalTripStops = this.orderStops.map((orderStop) => {
        const order = this.ordersById.get(orderStop.orderId);
        const existedTripStop = tripStopsByOrderStopId.get(orderStop.id);

        // if trip stop already existed use it
        if (existedTripStop) {
          return existedTripStop;
        }

        // if orderStop related to new order(after "Add order")
        // attach them to the end
        const isRelatedToNewOrder = orderStop?.orderId === this.newOrder?.id;

        if (isRelatedToNewOrder) {
          maxExistingPosition += 1;

          return this.tripManager.createTripStopFromOrderStop(orderStop, order, {
            position: maxExistingPosition,
          });
        }

        // if we have new order stop with position 1 add it as the first trip stop
        if (orderStop.position === 1) {
          return this.tripManager.createTripStopFromOrderStop(orderStop, order, {
            position: 0,
          });
        }

        // if new order stop does not have position 1
        // 1) find prev order stop
        // 2) find trip stop for this prev order stop
        // 3) add new trip stop after trip stop from second step
        const previousOrderStop = this.orderStops.find(
          (os) => os.orderId === orderStop.orderId && os.position === orderStop.position - 1,
        );

        const previousTripStop = previousOrderStop ? tripStopsByOrderStopId.get(previousOrderStop.id) : null;

        const newPosition = previousTripStop ? previousTripStop.position + 1 : Infinity;

        return this.tripManager.createTripStopFromOrderStop(orderStop, order, {
          position: newPosition,
        });
      });

      const reloadTripStops = this.trip.tripStops.filter((tripStop) => tripStop.isReload);

      this.tripStops = [...normalTripStops, ...reloadTripStops]
        .sort((a, b) => a.position - b.position)
        .map((tripStop, index) => ({
          // update position to escape position duplicates
          ...tripStop,
          position: index + 1,
        }));
    },
    createOrderColors() {
      this.orderColors = this.trip.orders.reduce((acc, order, index) => {
        acc[order.id] = orderColorList[index];
        return acc;
      }, {});
    },
    swapTripStopsPosition(firstStopId, secondStopId) {
      const first = this.tripStops.find((s) => s.id === firstStopId);
      const second = this.tripStops.find((s) => s.id === secondStopId);

      if (!first || !second) {
        return this.tripStops;
      }

      this.tripStops = [...this.tripStops]
        .map((stop) => {
          if (stop.id === firstStopId) {
            return { ...stop, position: second.position };
          }

          if (stop.id === secondStopId) {
            return { ...stop, position: first.position };
          }

          return stop;
        })
        .sort((a, b) => a.position - b.position);

      this.tripStops = this.tripManager.recalculateTripStopsWithNumberAndName(this.tripStops);
    },
    move(stop, direction) {
      const index = this.tripStops.findIndex((s) => s.id === stop.id);

      if (index === -1) return;

      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= this.tripStops.length) {
        return;
      }

      const targetStop = this.tripStops[targetIndex];

      this.swapTripStopsPosition(stop.id, targetStop.id);
    },
    closeReorderStopsDialog() {
      this.model = false;
      this.$emit("reorder", this.tripStops);
    },
    onTripStopSave(updatedStop) {
      this.tripStops = this.tripStops.map((stop) => {
        if (stop.id === updatedStop.id) {
          return updatedStop;
        }

        return stop;
      });
    },
  },
};
</script>

<template>
  <omni-dialog
    v-model="model"
    :primary-button-disabled="isPrimaryButtonDisabled"
    hide-close
    persistent
    primary-action-label="Save"
    title="Stops order"
    width="750"
    @primary-action:click="closeReorderStopsDialog"
  >
    <div class="trip-stop__info d-flex align-center flex-wrap mb-6">
      <div v-for="order in orders" :key="order.id" class="trip-stop__info-block d-flex align-center mr-4">
        <span :class="'square-' + orderColors[order.id]" class="trip-stop__info-color rounded mr-2 d-block" />
        <span class="text-grey-darken-3 font-size-12 font-weight-medium flex-none"> Order #{{ order.number }} </span>
      </div>

      <div v-if="hasReload" class="trip-stop__info-block d-flex align-center mr-4">
        <span class="trip-stop__info-color rounded mr-2 d-block square-reload" />
        <span class="text-grey-darken-3 font-size-12 font-weight-medium flex-none"> Reload stops </span>
      </div>
    </div>
    <div class="trip-stop__blocks">
      <reorder-trip-stop
        v-for="item in tripStops"
        :key="item.id"
        :order-freights="orderFreights"
        :orders="orders"
        :route-stops="routeStops"
        :travel-orders="travelOrders"
        :trip-stop="item"
        :trip-stops="tripStops"
        @move="move"
        @save="onTripStopSave"
      />
    </div>
  </omni-dialog>
</template>

<style lang="scss">
@use "@/assets/style/color" as *;

.trip-stop {
  &__info-color {
    width: 16px;
    height: 16px;
  }

  &__blocks {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
}

.square {
  &-primary {
    background-color: #536dfe !important;
  }

  &-success {
    background-color: #7cb342 !important;
  }

  &-warning {
    background-color: #ffab40 !important;
  }

  &-info {
    background-color: #ab47bc !important;
  }

  &-danger {
    background-color: #ff7043 !important;
  }

  &-secondary {
    background-color: #78909c !important;
  }

  &-light {
    background-color: #eceff1 !important;
  }

  &-dark {
    background-color: #37474f !important;
  }

  &-purple {
    background-color: #8e24aa !important;
  }

  &-teal {
    background-color: #26a69a !important;
  }

  &-reload {
    background-color: #ff9800 !important;
  }
}
</style>
